/**
 * Stockfish3.js - Dedicated WASM analysis handler for 3Board.jsx
 * Manages Stockfish engine analysis with caching and structured output.
 */

import { Chess } from 'chess.js';

class Stockfish3 {
  constructor() {
    this.worker = null;
    this.isAnalyzing = false;
    this.currentFEN = '';
    this.analysisDepth = 15; // Default depth
    this.multiPV = 3; // Default number of lines to analyze
    this.isEngineReady = false;
    this.pendingPositions = [];
    this.analysisData = {
      fen: '',
      depth: 0,
      lines: [],
      isComplete: false
    };
    this.analysisTimeout = null;
    this.analysisCache = {};
    this.lastSentAnalysis = null; // Prevent duplicate analysis results
    this.activeAnalysisRequests = new Set(); // Prevent feedback loop
    
    // Initialize global cache for direct access from components
    window.currentAnalysisCache = null;
    
    // Expose engine ready state globally
    window.stockfishEngineReady = false;
    
    // Bind methods
    this.init = this.init.bind(this);
    this.handleAnalysisRequest = this.handleAnalysisRequest.bind(this);
    this.handleWorkerMessage = this.handleWorkerMessage.bind(this);
    this.stopAnalysis = this.stopAnalysis.bind(this);
    this.sendCommand = this.sendCommand.bind(this);
    this.parseScore = this.parseScore.bind(this);
    
    // Initialize
    this.init();
  }
  
  /**
   * Initialize the Stockfish engine and set up event listeners
   */
  init() {
    try {
      // Test Chess.js availability for move conversion
      this.chessJSAvailable = this.testChessJSAvailability();
      
      // Create worker if it doesn't exist
      if (!this.worker) {
        // console.log('Stockfish3: Creating worker...');
        
        // Try different paths based on project structure
        try {
          // Use the same worker path that works in the project
          this.worker = new Worker('/stockfish-nnue-16-single.js');
        //   console.log('Stockfish3: Worker created using absolute path');
        } catch (e) {
          console.error('Stockfish3: Failed to create worker with absolute path:', e);
          try {
            this.worker = new Worker('./stockfish-nnue-16-single.js');
            // console.log('Stockfish3: Worker created using relative path');
          } catch (e2) {
            console.error('Stockfish3: Failed to create worker with relative path:', e2);
            try {
              // Last resort, try with PUBLIC_URL
              const publicUrl = process.env.PUBLIC_URL || '';
              this.worker = new Worker(`${publicUrl}/stockfish-nnue-16-single.js`);
            //   console.log('Stockfish3: Worker created using PUBLIC_URL');
            } catch (e3) {
              console.error('Stockfish3: All worker creation attempts failed:', e3);
              throw new Error('Could not initialize Stockfish worker');
            }
          }
        }
        
        this.worker.onmessage = this.handleWorkerMessage;
        this.worker.onerror = (error) => {
        //   console.error('Stockfish3: Worker error:', error);
        };
        
        // Initialize engine with staggered commands to ensure proper setup
        // console.log('Stockfish3: Initializing engine...');
        this.sendCommand('uci');
        
        // Delayed initialization to allow UCI to complete
        setTimeout(() => {
          this.sendCommand('setoption name MultiPV value ' + this.multiPV);
          this.sendCommand('setoption name Threads value 1');
          this.sendCommand('setoption name Hash value 128');
          // Disable NNUE evaluation to avoid errors with missing neural network file
          this.sendCommand('setoption name Use NNUE value false');
          this.sendCommand('isready');
        }, 300);
      }
      
      // Set up global event listeners
      window.addEventListener('request-stockfish-analysis', (e) => {
        // console.log('Stockfish3: Received analysis request event', e.detail);
        const { fen, forceCalculation = false } = e.detail;
        this.handleAnalysisRequest(fen, forceCalculation);
      });
      
      // Expose global function for direct calls
      window.updatePositionForAnalysis = (fen) => {
        // console.log('Stockfish3: updatePositionForAnalysis called with', fen);
        this.handleAnalysisRequest(fen, false);
      };
      
      // Remove test analysis - it can interfere with actual game analysis
      
    //   console.log('Stockfish3.js initialization complete');
    } catch (error) {
      console.error('Error initializing Stockfish3:', error);
    }
  }
  
  /**
   * Send a command to the Stockfish worker
   * @param {string} command - UCI command to send
   */
  sendCommand(command) {
    if (this.worker) {
    //   console.log('Stockfish3: Sending command:', command);
      this.worker.postMessage(command);
    } else {
      console.error('Stockfish3: Cannot send command, worker not initialized');
    }
  }
  
  /**
   * Check if there's cached analysis for the current position
   * @param {string} fen - FEN position to check
   * @returns {boolean} - Whether cached analysis exists
   */
  hasCachedAnalysis(fen) {
    // Check if window.currentAnalysisCache exists and matches the FEN
    if (window.currentAnalysisCache && 
        window.currentAnalysisCache.fen === fen &&
        window.currentAnalysisCache.pvs && 
        window.currentAnalysisCache.pvs.length > 0) {
    //   console.log('Stockfish3: Found cached analysis for', fen);
      return true;
    }
    return false;
  }
  
  /**
   * Handle analysis request for a position
   * @param {string} fen - FEN position to analyze
   * @param {boolean} forceCalculation - Whether to force recalculation
   */
  handleAnalysisRequest(fen, forceCalculation = false) {
    if (!fen) {
      console.warn('Stockfish3: Empty FEN provided to handleAnalysisRequest');
      return;
    }
    
    // console.log('Stockfish3: Handling analysis request for FEN:', fen, 'force:', forceCalculation);
    
    // CRITICAL: Prevent feedback loop by checking if we're already analyzing this position
    const analysisKey = `${fen}_${forceCalculation}`;
    if (this.activeAnalysisRequests.has(analysisKey) && !forceCalculation) {
      // console.log('Stockfish3: PREVENTING FEEDBACK LOOP - already analyzing', analysisKey);
      return;
    }
    
    // Clean up FEN (sometimes FENs have extra spaces or annotations)
    const cleanFEN = fen.split(' ').slice(0, 6).join(' ');
    const cleanAnalysisKey = `${cleanFEN}_${forceCalculation}`;
    
    // If engine is not ready, queue this position WITHOUT adding to activeAnalysisRequests
    if (!this.isEngineReady) {
      // console.log('Stockfish3: Engine not ready, queueing position:', cleanFEN);
      this.pendingPositions.push({fen: cleanFEN, force: forceCalculation});
      return;
    }
    
    // Add to active analysis requests to prevent feedback loop (only when engine is ready)
    this.activeAnalysisRequests.add(cleanAnalysisKey);
    // console.log('Stockfish3: Added to activeAnalysisRequests:', cleanAnalysisKey);
    
    // Check if we're already analyzing this position
    if (this.isAnalyzing && this.currentFEN === cleanFEN && !forceCalculation) {
      // console.log('Stockfish3: Already analyzing this position:', cleanFEN);
      return;
    }
    
    // Check for cached analysis
    if (!forceCalculation && this.hasCachedAnalysis(cleanFEN)) {
    //   console.log('Stockfish3: Using cached analysis for', cleanFEN);
      
      // Create Analysis object from cache
      const cachedAnalysis = window.currentAnalysisCache;
      const lines = [];
      
      // Format cached analysis into the expected structure
      if (cachedAnalysis.pvs && cachedAnalysis.pvs.length > 0) {
        for (let i = 0; i < cachedAnalysis.pvs.length; i++) {
          const pv = cachedAnalysis.pvs[i];
          const score = cachedAnalysis.scores ? cachedAnalysis.scores[i] : null;
          
          if (pv && pv.length > 0) {
            lines.push({
              move: pv[0],
              score: score || '0.0',
              pv: pv
            });
          }
        }
      }
      
      // Send the cached analysis
      const result = {
        fen: cleanFEN,
        depth: cachedAnalysis.depth || 0,
        lines: lines,
        isComplete: true,
        isFromCache: true
      };
      
    //   console.log('Stockfish3: Sending cached analysis result', result);
      
      if (typeof window.receiveStockfishAnalysis === 'function') {
        window.receiveStockfishAnalysis(result);
      } else {
        console.error('Stockfish3: window.receiveStockfishAnalysis is not a function');
      }
      
      return;
    }
    
    // Stop any ongoing analysis
    this.stopAnalysis();
    
    // Reset analysis data
    this.analysisData = {
      fen: cleanFEN,
      depth: 0,
      lines: [],
      isComplete: false
    };
    
    // Start new analysis
    this.currentFEN = cleanFEN;
    this.isAnalyzing = true;
    
    // Get depth setting from context if available
    const desiredDepth = window.stockfishSettings?.depth || this.analysisDepth;
    
    // Start analysis
    // console.log('🎯 Stockfish3: Starting analysis of', cleanFEN, 'at depth', desiredDepth);
    // console.log('🎯 UCI Commands being sent:');
    this.sendCommand('ucinewgame'); // Reset engine state
    // console.log('   ✅ ucinewgame');
    this.sendCommand('isready'); // Wait for engine to be ready
    // console.log('   ✅ isready');
    // Note: MultiPV is already set during initialization to this.multiPV
    this.sendCommand('position fen ' + cleanFEN);
    // console.log('   ✅ position fen', cleanFEN);
    this.sendCommand('go depth ' + desiredDepth);
    // console.log('   ✅ go depth', desiredDepth);
    
    // Set a timeout to force completion after a reasonable time
    // This ensures we always get a result even if bestmove is never received
    if (this.analysisTimeout) {
      clearTimeout(this.analysisTimeout);
    }
    
    this.analysisTimeout = setTimeout(() => {
      if (this.isAnalyzing && this.analysisData.lines.length > 0) {
        // console.log('Stockfish3: Analysis timeout reached, forcing completion');
        this.stopAnalysis();
        this.analysisData.isComplete = true;
        this.sendAnalysisResult();
      }
    }, 10000); // 10 seconds timeout
  }
  
  /**
   * Stop the current analysis
   */
  stopAnalysis() {
    if (this.analysisTimeout) {
      clearTimeout(this.analysisTimeout);
      this.analysisTimeout = null;
    }
    
    if (this.isAnalyzing) {
    //   console.log('Stockfish3: Stopping current analysis');
      this.sendCommand('stop');
      this.isAnalyzing = false;
    }
  }
  
  /**
   * Parse score from Stockfish output
   * @param {string} scoreType - 'cp' (centipawns) or 'mate'
   * @param {string} value - Score value
   * @returns {string} - Formatted score
   */
  parseScore(scoreType, value) {
    if (scoreType === 'cp') {
      const score = parseInt(value) / 100;
      return (score > 0 ? '+' : '') + score.toFixed(2);
    } else if (scoreType === 'mate') {
      return 'M' + value;
    }
    return '0.00';
  }
  
  /**
   * Process any pending position requests
   */
  processPendingPositions() {
    if (this.pendingPositions.length > 0 && this.isEngineReady) {
      // console.log('Stockfish3: Processing pending positions:', this.pendingPositions.length);
      const nextPosition = this.pendingPositions.shift();
      // console.log('Stockfish3: Processing queued position:', nextPosition.fen, 'force:', nextPosition.force);
      this.handleAnalysisRequest(nextPosition.fen, nextPosition.force);
    }
  }
  
  /**
   * Handle messages from the Stockfish worker
   * @param {MessageEvent} event - Worker message event
   */
  handleWorkerMessage(event) {
    if (!event || !event.data) return;
    
    const data = event.data;
    
    // Ignore non-string messages
    if (typeof data !== 'string') {
    //   console.log('Stockfish3: Received non-string message:', data);
      return;
    }
    
    // console.log('Stockfish3: Worker message:', data);
    
    // Check for readyok message
    if (data === 'readyok') {
    //   console.log('Stockfish3: Engine is ready');
      this.isEngineReady = true;
      window.stockfishEngineReady = true;
      
      // Dispatch engine ready event for UI components
      window.dispatchEvent(new CustomEvent('stockfish-engine-ready', {
        detail: { ready: true }
      }));
      
      this.processPendingPositions();
      return;
    }
    
    // Process the UCI output
    if (data.includes('info depth')) {
      // Add debugging for raw UCI message
    //   console.log('🔍 Raw UCI message:', data);
      
      // Parse analysis info with more flexible regex patterns
      const matches = {
        depth: data.match(/depth (\d+)/),
        multipv: data.match(/multipv (\d+)/),
        // Handle both formats of score: integer and float
        scoreType: data.match(/score (cp|mate) (-?\d+(\.\d+)?)/),
        pv: data.match(/pv (.+)$/)
      };
      
      if (matches.depth && matches.multipv && matches.scoreType && matches.pv) {
        const depth = parseInt(matches.depth[1]);
        const multipv = parseInt(matches.multipv[1]);
        const scoreType = matches.scoreType[1];
        const rawScoreValue = matches.scoreType[2];
        
        // FIXED: Extract PV moves correctly - everything after "pv "
        const pvString = matches.pv[1].trim();
        const pvMoves = pvString.split(' ').filter(move => {
          // Filter out empty strings and validate chess move format
          return move.length >= 4 && move.match(/^[a-h][1-8][a-h][1-8][qrbn]?$/);
        });
        
        // console.log(`🔍 PV${multipv} Debug - Raw PV string: "${pvString}"`);
        // console.log(`🔍 PV${multipv} Debug - Filtered moves:`, pvMoves);
        
        // ENHANCED: Get user perspective and position info for proper score normalization
        const currentFEN = this.currentFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
        const isBlackToMove = currentFEN.split(' ')[1] === 'b';
        
        // Get user orientation - default to white if not available
        let userOrientation = 'white';
        try {
          // Try to get orientation from various sources
          if (window.state?.orientation) {
            userOrientation = window.state.orientation;
          } else if (window.getGameState && typeof window.getGameState === 'function') {
            const gameState = window.getGameState();
            userOrientation = gameState?.orientation || 'white';
          }
        } catch (e) {
          // Default to white if we can't determine orientation
          userOrientation = 'white';
        }
        
        const userPlayingWhite = userOrientation === 'white';
        
        // ENHANCED: Normalize score to user's perspective
        let normalizedScore;
        let normalizedCP = null;
        
        if (scoreType === 'cp') {
          // Parse centipawn score and convert to pawn units
          let cpScore = parseInt(rawScoreValue) / 100;
          
          // Normalize to user's perspective (always positive when user is winning)
          if (userPlayingWhite) {
            // User is white - if it's black's turn, negate to get white's perspective
            normalizedCP = isBlackToMove ? -cpScore : cpScore;
          } else {
            // User is black - if it's white's turn, negate to get black's perspective
            normalizedCP = isBlackToMove ? cpScore : -cpScore;
          }
          
          normalizedScore = normalizedCP >= 0 ? `+${normalizedCP.toFixed(2)}` : normalizedCP.toFixed(2);
        } else if (scoreType === 'mate') {
          // Parse mate score
          let rawMateIn = parseInt(rawScoreValue);
          let normalizedMateIn;
          
          if (userPlayingWhite) {
            normalizedMateIn = isBlackToMove ? -rawMateIn : rawMateIn;
          } else {
            normalizedMateIn = isBlackToMove ? rawMateIn : -rawMateIn;
          }
          
          normalizedScore = `M${normalizedMateIn}`;
          normalizedCP = normalizedScore; // Store mate score as string
        }
        
        // Extract best move and convert to SAN
        const bestMoveLAN = pvMoves.length > 0 ? pvMoves[0] : null;
        
        if (!bestMoveLAN) {
          console.warn(`❌ No valid moves found in PV${multipv} - Raw PV: "${pvString}"`);
          console.warn(`❌ Filtered moves array:`, pvMoves);
          return; // Skip this line if no valid moves
        }
        
        // Convert to SAN using Chess.js - use analysisData.fen as primary source
        const fenToUse = this.analysisData?.fen || this.currentFEN || currentFEN;
        // console.log(`🔄 About to convert LAN to SAN - FEN: ${fenToUse}, Move: ${bestMoveLAN}`);
        // console.log(`🔍 Debug FEN sources - analysisData.fen: ${this.analysisData?.fen}, this.currentFEN: ${this.currentFEN}, currentFEN: ${currentFEN}`);
        
        const bestMoveSAN = this.convertLANtoSAN(bestMoveLAN, fenToUse);
        
        // console.log(`✅ PV${multipv} Conversion result - LAN: ${bestMoveLAN}, SAN: ${bestMoveSAN}`);
        
        // Store enhanced data for final comprehensive logging
        const enhancedLineData = {
          multipv,
          depth,
          normalizedCP,
          normalizedScore,
          pvMoves,
          bestMoveLAN,
          bestMoveSAN,
          rawScoreValue,
          scoreType,
          userOrientation,
          positionTurn: isBlackToMove ? 'black' : 'white'
        };
        
        // Update the max depth we've reached
        this.analysisData.depth = Math.max(this.analysisData.depth, depth);
        
        // Use the normalized score
        const formattedScore = normalizedScore;
        
        // Update or add the line
        const lineIndex = multipv - 1;
        
        // Ensure the lines array is big enough
        while (this.analysisData.lines.length <= lineIndex) {
          this.analysisData.lines.push({
            move: '',
            score: '0.00',
            pv: []
          });
        }
        
        // Update the line - only if we have a valid SAN move
        if (bestMoveSAN) {
          this.analysisData.lines[lineIndex] = {
            move: pvMoves[0],
            score: formattedScore,
            pv: pvMoves,
            // Store additional data for enhanced logging
            normalizedCP: normalizedCP,
            bestMoveLAN: bestMoveLAN,
            bestMoveSAN: bestMoveSAN,
            userOrientation: userOrientation,
            positionTurn: isBlackToMove ? 'black' : 'white'
          };
        } else {
          // Skip this line if we couldn't convert to SAN
          // console.warn(`⚠️ Skipping PV${multipv} line due to failed SAN conversion`);
          return;
        }
        
        // Send partial results at depth 10 (changed from 6) for better user experience
        if (depth === 10) {
          const completePVs = this.analysisData.lines.filter(line => line && line.move && line.bestMoveSAN);
          if (completePVs.length >= 2) { // At least 2 PVs for partial results
            const partialResultKey = `${this.analysisData.fen}_${depth}_partial`;
            if (this.lastSentAnalysis !== partialResultKey) {
              // console.log(`Stockfish3: Sending partial results at depth ${depth}`);
              this.lastSentAnalysis = partialResultKey;
              
              const partialResult = {
                fen: this.analysisData.fen,
                depth: depth,
                lines: this.analysisData.lines,
                isComplete: false
              };
              
              if (typeof window.receiveStockfishAnalysis === 'function') {
                window.receiveStockfishAnalysis(partialResult);
              }
            }
          }
        }
        
        // Send final results when we have all 3 PVs at target depth
        if (depth === this.analysisDepth) {
          const completePVs = this.analysisData.lines.filter(line => line && line.move && line.bestMoveSAN);
          if (completePVs.length === 3) {
            // Don't send here - wait for bestmove to send final results
            // This prevents the double-send issue
            // console.log(`Stockfish3: All 3 PVs complete at target depth ${this.analysisDepth}, waiting for bestmove`);
          }
        }
      }
    } else if (data.startsWith('bestmove')) {
      // Analysis is complete
      // console.log('Stockfish3: Analysis complete, received bestmove:', data);
      this.isAnalyzing = false;
      this.analysisData.isComplete = true;
      
      // Clear the timeout since we got a proper completion
      if (this.analysisTimeout) {
        clearTimeout(this.analysisTimeout);
        this.analysisTimeout = null;
      }
      
      // Remove from active analysis requests to allow future analysis
      const cleanFEN = this.currentFEN?.split(' ').slice(0, 6).join(' ');
      if (cleanFEN) {
        this.activeAnalysisRequests.delete(`${cleanFEN}_false`);
        this.activeAnalysisRequests.delete(`${cleanFEN}_true`);
      }
      
      // Send the complete analysis
      this.sendAnalysisResult();
    }
  }
  
  /**
   * Check if Chess.js is available and functional
   */
  testChessJSAvailability() {
    try {
      // Test basic functionality with imported Chess class
      const testChess = new Chess();
      const testMove = testChess.move('e4');
      
      if (testMove && testMove.san === 'e4') {
        // console.log('✅ Chess.js is available and functional');
        return true;
      } else {
        // console.warn('🚨 Chess.js found but not functioning correctly');
        return false;
      }
      
    } catch (error) {
    //   console.error('🚨 Error testing Chess.js:', error);
      return false;
    }
  }

  /**
   * Convert LAN move to SAN using chess.js
   */
  convertLANtoSAN(lanMove, fen) {
    try {
      // Basic FEN validation - must have 6 space-separated fields
      if (!fen || typeof fen !== 'string') {
        console.warn(`🚨 Invalid FEN provided (not a string): ${fen}`);
        return null; // Return null instead of LAN when FEN is invalid
      }
      
      const fenParts = fen.split(' ');
      if (fenParts.length !== 6) {
        console.warn(`🚨 Invalid FEN provided (${fenParts.length} fields instead of 6): ${fen}`);
        return null; // Return null instead of LAN when FEN is invalid
      }
      
      // Chess.js is now imported at the top of the file
      if (!Chess) {
        console.warn('🚨 Chess.js import failed');
        return null;
      }
      
    //   console.log(`🎯 Chess.js is available, proceeding with conversion`);
      
    //   console.log(`🔄 Converting ${lanMove} to SAN using FEN: ${fen}`);
      
      // Create a new chess instance with the current position
      const chess = new Chess(fen);
      
      // Validate the FEN was loaded correctly
      if (!chess.fen()) {
        console.warn(`🚨 Invalid FEN provided: ${fen}`);
        return null; // Return null instead of LAN
      }
      
      // console.log(`📋 Chess instance created successfully. Current turn: ${chess.turn()}`);
      
      // Attempt to make the move and get SAN notation
      const moveResult = chess.move(lanMove);
      
      if (moveResult) {
        // console.log(`✅ Successfully converted ${lanMove} → ${moveResult.san}`);
        return moveResult.san;
      } else {
        // console.warn(`🚨 Invalid move ${lanMove} for position ${fen}`);
        
        // Try alternative move formats in case the LAN format is slightly different
        const alternativeFormats = [
          lanMove.toLowerCase(),
          lanMove.toUpperCase(),
        ];
        
        for (const altMove of alternativeFormats) {
          // Reset chess instance for each attempt
          const chessAlt = new Chess(fen);
          const altResult = chessAlt.move(altMove);
          if (altResult) {
            // console.log(`✅ Alternative format worked: ${altMove} → ${altResult.san}`);
            return altResult.san;
          }
        }
        
        // If all else fails, try parsing the move manually
        if (lanMove.length >= 4) {
          const from = lanMove.substring(0, 2);
          const to = lanMove.substring(2, 4);
          const promotion = lanMove.length > 4 ? lanMove.substring(4) : undefined;
          
          const chessObj = new Chess(fen);
          const moveObj = {
            from: from,
            to: to,
            promotion: promotion
          };
          
          const objResult = chessObj.move(moveObj);
          if (objResult) {
            // console.log(`✅ Object format worked: ${JSON.stringify(moveObj)} → ${objResult.san}`);
            return objResult.san;
          }
        }
        
        // console.warn(`❌ All conversion attempts failed for ${lanMove}`);
        return null; // Return null instead of LAN when all attempts fail
      }
      
    } catch (error) {
      // console.error(`🚨 Error converting ${lanMove} to SAN:`, error);
      // console.error(`🚨 FEN was: ${fen}`);
      return null; // Return null instead of LAN when there's an error
    }
  }

  /**
   * Send the analysis result to the registered callback
   */
  sendAnalysisResult() {
    // console.log('Stockfish3: sendAnalysisResult called');
    if (!this.analysisData || !this.analysisData.lines || this.analysisData.lines.length === 0) {
      console.warn('Stockfish3: No analysis data to send - analysisData:', !!this.analysisData, 'lines:', this.analysisData?.lines?.length);
      return;
    }
    
    // Prevent duplicate analysis results for same position
    const finalResultKey = `${this.analysisData.fen}_${this.analysisData.depth}_final`;
    if (this.lastSentAnalysis === finalResultKey) {
      // console.log('Stockfish3: Skipping duplicate final analysis result for', finalResultKey);
      return;
    }
    this.lastSentAnalysis = finalResultKey;
    
    // Only send results if we have valid SAN conversions for all lines
    const validLines = this.analysisData.lines.filter(line => line && line.move && line.bestMoveSAN);
    if (validLines.length === 0) {
      // console.warn('Stockfish3: No valid SAN conversions available, keeping "Calculating..." state');
      return;
    }
    
    // If we don't have 3 complete lines with SAN conversions, don't send yet
    if (validLines.length < 3) {
      // console.warn(`Stockfish3: Only ${validLines.length}/3 lines have valid SAN conversions, waiting for more...`);
      return;
    }
    
    // 🚀 COMPREHENSIVE STOCKFISH3 ANALYSIS SUMMARY
    // console.log('\n🚀 =============== STOCKFISH3 ANALYSIS COMPLETE ===============');
    // console.log(`📍 Position: ${this.analysisData.fen}`);
    // console.log(`🎯 Depth: ${this.analysisData.depth}`);
    // console.log('📊 Analysis Results:');
    
    this.analysisData.lines.forEach((line, index) => {
      const lineNum = index + 1;
      const score = (line.normalizedCP !== undefined && 
                     typeof line.normalizedCP === 'number' && 
                     !isNaN(line.normalizedCP)) 
        ? `+${line.normalizedCP.toFixed(2)}` 
        : line.score;
      
    //   console.log(`\n   🔍 PV${lineNum}:`);
    //   console.log(`      cp=${score} (normalized to user perspective)`);
    //   console.log(`      depth=${this.analysisData.depth}`);
    //   console.log(`      bestmove_LAN=${line.bestMoveLAN || line.move}`);
    //   console.log(`      bestmove_SAN=${line.bestMoveSAN || line.move}`);
    //   console.log(`      pv=${line.pv ? line.pv.join(' ') : 'N/A'} (LAN format)`);
    //   console.log(`      user_orientation=${line.userOrientation || 'unknown'}`);
    //   console.log(`      position_turn=${line.positionTurn || 'unknown'}`);
    });
    
    // console.log('===============================================================\n');
    

    
    // Cache the result for later use
    this.analysisCache[this.analysisData.fen] = { 
      ...this.analysisData,
      timestamp: Date.now()
    };
    
    // Format the result for sending to the callback with enhanced normalized data
    const result = {
      fen: this.analysisData.fen,
      depth: this.analysisData.depth,
      lines: this.analysisData.lines,
      isComplete: this.analysisData.isComplete,
      
      // Enhanced normalized data for UI consumption
      pvs: this.analysisData.lines.map((line, index) => ({
        line: index + 1,
        evaluation: line.score,
        normalizedCP: line.normalizedCP, // Keep all normalizedCP values (numbers AND mate score strings)
        bestMoveLAN: line.bestMoveLAN || line.move,
        bestMoveSAN: line.bestMoveSAN || line.move,
        pv: line.pv || [],
        pvSAN: line.pvSAN || [], // Will be enhanced later if needed
        userOrientation: line.userOrientation,
        positionTurn: line.positionTurn,
        depth: this.analysisData.depth
      })),
      
      scores: this.analysisData.lines.map(line => line.score),
      positionFEN: this.analysisData.fen,
      timestamp: Date.now(),
      
      // Add a flag to indicate this is just an analysis update, not a request to change the board position
      isAnalysisOnly: true
    };
    
    // console.log('Stockfish3: Sending analysis result to callback');
    
    // Also store the result globally for direct access
    window.currentAnalysisCache = result;
    
    // Store the current FEN to prevent board resets
    const originalFen = window.currentFEN;
    
    // Send to the callback if registered
    // console.log('Stockfish3: About to call receiveStockfishAnalysis callback');
    // console.log('Stockfish3: Callback exists:', typeof window.receiveStockfishAnalysis === 'function');
    // console.log('Stockfish3: Result has pvs:', !!(result.pvs && result.pvs.length > 0));
    
    if (typeof window.receiveStockfishAnalysis === 'function') {
      // console.log('Stockfish3: Calling receiveStockfishAnalysis with result');
      window.receiveStockfishAnalysis(result);
    } else {
      console.error('Stockfish3: window.receiveStockfishAnalysis is not a function');
    }
    
    // Ensure the current FEN wasn't changed by the callback
    if (window.currentFEN !== originalFen && originalFen) {
      console.warn('Stockfish3: Board position was reset during analysis completion. Restoring original position.');
      window.currentFEN = originalFen;
      
      // Dispatch a custom event to notify of an unauthorized position change
      try {
        const event = new CustomEvent('stockfish-position-preserved', {
          detail: { fen: originalFen }
        });
        window.dispatchEvent(event);
      } catch (e) {
        console.error('Stockfish3: Error dispatching position preservation event:', e);
      }
    }

    // Send the main analysis result event (existing)
    window.dispatchEvent(new CustomEvent('stockfish-analysis-result', {
      detail: result
    }));

    // NEW: Send direct evaluation score to StockfishEval components
    const bestLineEvaluation = this.getBestLineEvaluation();
    window.dispatchEvent(new CustomEvent('stockfish-evaluation-update', {
      detail: {
        evaluation: bestLineEvaluation,
        fen: this.currentFEN,
        timestamp: Date.now()
      }
    }));

    // console.log(`✅ Analysis complete for FEN: ${this.currentFEN.slice(0, 20)}...`);
  }
  
  getBestLineEvaluation() {
    if (!this.analysisData || !this.analysisData.lines || this.analysisData.lines.length === 0) {
      return 0;
    }

    const bestLine = this.analysisData.lines[0];
    
    // Try normalizedCP first (could be number or mate score string)
    if (bestLine.normalizedCP !== undefined && bestLine.normalizedCP !== null) {
      // If it's already a mate score string, return it
      if (typeof bestLine.normalizedCP === 'string' && bestLine.normalizedCP.startsWith('M')) {
        return bestLine.normalizedCP;
      }
      // If it's a number, return it
      if (typeof bestLine.normalizedCP === 'number' && !isNaN(bestLine.normalizedCP)) {
        return bestLine.normalizedCP;
      }
    }
    
    // Fall back to parsing the score string
    if (bestLine.score && typeof bestLine.score === 'string') {
      // Handle mate scores
      if (bestLine.score.includes('M')) {
        return bestLine.score; // Return mate scores as strings
      }
      
      // Parse numeric scores like "+1.45", "-0.72", etc.
      const numericScore = parseFloat(bestLine.score.replace('+', ''));
      if (!isNaN(numericScore)) {
        return numericScore;
      }
    }
    
    return 0;
  }
  
  /**
   * Clean up resources
   */
  destroy() {
    // Remove event listeners
    window.removeEventListener('request-stockfish-analysis', this.handleAnalysisRequest);
    
    // Clear any pending timeouts
    if (this.analysisTimeout) {
      clearTimeout(this.analysisTimeout);
      this.analysisTimeout = null;
    }
    
    // Terminate worker
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    
    // Clean up global references
    delete window.updatePositionForAnalysis;
    
    // console.log('Stockfish3: Resources destroyed');
  }
}

// Create singleton instance
const stockfish3Instance = new Stockfish3();

export default stockfish3Instance; 